"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

export default function LocalExpertise() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("revealed"); }); },
      { threshold: 0.1 }
    );
    const els = ref?.current?.querySelectorAll(".reveal-hidden");
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const stats = [
    { label: "Years in PCB market", value: "4+" },
    { label: "Local contractor network", value: "30+" },
    { label: "Avg. occupancy rate", value: "78%" },
    { label: "Revenue vs. self-mgmt", value: "+28%" },
  ];

  return (
    <section ref={ref} className="section-padding bg-sand-dark">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-hidden">
            <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">Local Expertise</span>
            <h2 className="font-heading text-4xl text-navy mb-6">
              We Know PCB Better Than{" "}
              <span className="italic font-light text-gradient-gold">Anyone</span>
            </h2>
            <div className="space-y-4 text-navy/65 text-sm leading-relaxed mb-8">
              <p>Panama City Beach has unique seasonal demand patterns, a distinct guest demographic, and local regulations that out-of-town management companies simply don&apos;t understand.</p>
              <p>We know that Spring Break drives premium rates in March–April. We know that Gulf-front properties command 40% more than second-row. We know the fastest HVAC contractor when AC fails on a July Friday at 8pm.</p>
              <p>That local intelligence is something you can&apos;t outsource. It&apos;s why our managed properties consistently outperform self-managed competitors in the same building.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {stats?.map((item) => (
                <div key={item?.label} className="bg-white rounded-2xl p-4 border border-sand-medium">
                  <div className="font-heading text-2xl font-bold text-gold mb-1">{item?.value}</div>
                  <div className="text-navy/55 text-xs">{item?.label}</div>
                </div>
              ))}
            </div>
            <Link href="/contact" className="btn-navy inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm group">
              Work With Local Experts
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          <div className="reveal-hidden">
            <div className="bg-navy rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
              <h3 className="font-heading text-2xl text-white mb-6">PCB Seasonal Intelligence</h3>
              <div className="space-y-4">
                {[
                  { month: "Mar – Apr", label: "Spring Break Peak", rate: "95%", color: "bg-gold" },
                  { month: "Jun – Aug", label: "Summer High Season", rate: "92%", color: "bg-gold" },
                  { month: "May & Sep", label: "Shoulder Season", rate: "74%", color: "bg-blue-400" },
                  { month: "Oct – Nov", label: "Fall Value Season", rate: "58%", color: "bg-emerald-400" },
                  { month: "Dec – Feb", label: "Off-Season (Optimized)", rate: "45%", color: "bg-purple-400" },
                ]?.map((row) => (
                  <div key={row?.month} className="flex items-center gap-4">
                    <div className="w-20 shrink-0">
                      <span className="text-white/50 text-xs">{row?.month}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white text-xs font-medium">{row?.label}</span>
                        <span className="text-gold text-xs font-bold">{row?.rate}</span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${row?.color} rounded-full transition-all duration-700`}
                          style={{ width: row?.rate }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-white/30 text-xs mt-6">Occupancy rates for Premier-managed properties, 2025 average</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}